export interface HasFreeFields {
    Free?: number;
    FreeSpots?: number;
}

export interface HasExceptionTextsFields {
    ExceptionTexts?: Array<{
        Reason?: string;
        ReasonPublic?: string;
    }>;
}

export type TimeSlotAvailability = HasFreeFields & HasExceptionTextsFields;

/**
 * Returns the number of free bookable spots for a time slot.
 *
 * Prefer `FreeSpots` when available (it reflects actual spots left),
 * otherwise fall back to `Free` for backwards compatibility.
 */
export const getFreeSpots = (
    time: HasFreeFields | null | undefined
): number => {
    if (!time) return 0;

    if (typeof time.FreeSpots === "number") {
        return time.FreeSpots;
    }

    return typeof time.Free === "number" ? time.Free : 0;
};

export const getPublicExceptionReason = (
    time: HasExceptionTextsFields | null | undefined
): string | null => {
    if (!time?.ExceptionTexts?.length) {
        return null;
    }

    const publicException = time.ExceptionTexts.find(
        (exception) =>
            typeof exception?.ReasonPublic === "string" &&
            exception.ReasonPublic.trim().length > 0
    );

    return publicException?.ReasonPublic?.trim() || null;
};

export const hasTimeException = (
    time: HasExceptionTextsFields | null | undefined
): boolean => {
    if (!time?.ExceptionTexts?.length) {
        return false;
    }

    return time.ExceptionTexts.some(
        (e) =>
            (typeof e?.Reason === "string" && e.Reason.trim().length > 0) ||
            (typeof e?.ReasonPublic === "string" && e.ReasonPublic.trim().length > 0)
    );
};

export const canAddToQueue = (
    time: TimeSlotAvailability | null | undefined,
    enableBookingQueue?: boolean
): boolean => {
    const free = getFreeSpots(time);
    return !!(free === 0 && enableBookingQueue && !hasTimeException(time));
};
